import { Injectable } from '@angular/core';
import { KalDialogConfig, KalDialogService } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';
import {
    LetteredBookEntriesDialogComponent,
    LetteredBookEntriesDialogData
} from 'src/app/_shared/dialogs/lettered-book-entries-dialog/lettered-book-entries-dialog.component';
import { UserState } from 'src/app/_user/store/user/user.state';
import { arrayEmpty, arrayNotEmpty } from 'src/app/utils/array.utils';
import {
    ArrayOfCompteEcritureDto,
    CompteEcritureDtoInput,
    EcheancierModelDtoInput,
    LettrageModel,
    MutationDelettrerEcrituresArgs,
    MutationGetIdExerciceOfEcritureArgs,
    MutationGetListeCompteEcritureGeneriqueArgs,
    MutationGetListeLettrageModelArgs,
    MutationSupprimerEcheancierArgs,
    MutationUpdateEcheancierArgs
} from 'src/graphql/generated-types';

@Injectable({
    providedIn: 'root'
})
export class ScheduleService {
    constructor(private readonly graphqlService: GraphqlService,
                private readonly dialogService: KalDialogService,
                private readonly store: Store) {
    }

    /**
     * check and display lettering
     */
    getLetteringEntries$(scheduleId: number): Observable<LettrageModel[]> {
        return this.getScheduleEntries(scheduleId).pipe(
            switchMap(listeCompteEcritureGenerique => {
                const entriesList = listeCompteEcritureGenerique?.CompteEcritureDTO || [];
                const lettersList = this.presenceOfLettering(entriesList);
                return this.getCorrespondingExerciseId(entriesList).pipe(
                    map((exerciceId) => {
                        return {exerciceId, lettersList};
                    })
                );
            }),
            switchMap(({exerciceId, lettersList}) => {
                if (arrayNotEmpty(lettersList)) {
                    return this.getEntriesList(exerciceId, lettersList);
                }
                return of(null);
            }),
        );
    }

    /**
     * Suppression: Récupération des écritures de l'échéancier
     * @param scheduleId number
     */
    getScheduleEntries(scheduleId: number): Observable<ArrayOfCompteEcritureDto> {
        return this.graphqlService.mutateOne<'GetListeCompteEcritureGenerique', MutationGetListeCompteEcritureGeneriqueArgs>({
            mutation: gql`
                mutation ScheduleEcrituresList($contexte: ContextInfosInput, $critere: CriteresCompteEcritureDTOInput) {
                    GetListeCompteEcritureGenerique(
                        contexte: $contexte
                        critere: $critere
                    ) {
                        CompteEcritureDTO {
                            Id
                            ListeMouvementComptable {
                                MouvementComptableDTO {
                                    Lettre
                                }
                            }
                        }
                    }
                }
            `,
            variables: {
                critere: {
                    IdEcheancier: scheduleId,
                    WithSupprime: false,
                    TakeCompteMouvement: true
                }
            }
        });
    }

    /**
     * Suppression: Vérification de la présence de lettrage sur les écritures
     * @param entriesList string[]
     */
    presenceOfLettering(entriesList: CompteEcritureDtoInput[]): string[] {
        const lettersList: string[] = entriesList.flatMap(ecriture => {
            return ecriture.ListeMouvementComptable.MouvementComptableDTO.map(m => m.Lettre);
        });
        const uniqueSet = new Set(lettersList);
        return [...uniqueSet].filter(fil => fil !== null && fil !== '');
    }

    /**
     * Suppression: Affichage des écritures lettrées à supprimer
     */
    // Récupération de l'exercice correspondant
    getCorrespondingExerciseId(entriesList: CompteEcritureDtoInput[]): Observable<number> {
        const entrieId = entriesList[0]?.Id;
        return this.graphqlService.mutateOne<'GetIdExerciceOfEcriture', MutationGetIdExerciceOfEcritureArgs>({
            mutation: gql`
                mutation ExerciceOfEcriture($contexte: ContextInfosInput,
                    $IdEcriture: Float) {
                    GetIdExerciceOfEcriture(
                        contexte: $contexte
                        IdEcriture: $IdEcriture
                    )
                }
            `,
            variables: {
                IdEcriture: entrieId
            }
        });
    }


    // Récupération des écritures
    getEntriesList(exerciceId: number, lettersList: string[]): Observable<LettrageModel[]> {
        return this.graphqlService.mutateOne<'GetListeLettrageModel', MutationGetListeLettrageModelArgs>({
            mutation: gql`
                mutation GetListeLettrageModel($contexte: ContextInfosInput,
                    $Criteres: CriteresMouvementComptableDTOInput) {
                    GetListeLettrageModel(
                        contexte: $contexte
                        Criteres: $Criteres
                    ){
                        NombreElementTotal
                        ListeLettrageModel {
                            LettrageModel {
                                IdEcriture
                                Lettre
                                Utilisateur,
                                NomDelegCibleEcriture
                                CodeJournal
                                NumeroPiece
                                DateEcriture
                                CompteCollectif
                                NomPrenomBenef
                                LibelleMouvement
                                Debit
                                Credit
                            }
                        }
                    }
                }
            `,
            variables: {
                Criteres: {
                    IdExercice: exerciceId,
                    ListeLettreComptable: {
                        string: lettersList
                    },
                    IdCECibleSource: this.store.selectSnapshot(UserState.userContext).CurrentCE,
                    IsSupprime: false,
                    IsEcritureSimulee: false
                }
            }
        }).pipe(
            map(GetListeLettrageModel => {
                return GetListeLettrageModel?.ListeLettrageModel?.LettrageModel || [];
            })
        );
    }


    /**
     * Suppression: Modale qui confirme la suppression
     */
    openConfirmDeletingDialog(): Observable<any> {
        const config = new KalDialogConfig<ConfirmDialogData>({
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            title: 'Supprimer l\'échéancier',
            data: {
                cancelLabel: 'Annuler',
                submitLabel: 'Supprimer',
                body: 'Souhaitez-vous supprimer cet échéancier ?'
            }
        });

        return this.dialogService.open(ConfirmDialogComponent, config).afterClosed.pipe(
            filter(data => data?.accepted)
        );
    }

    /**
     * Suppression: Modale qui affiche les écritures lettrées à supprimer
     * @param letteredBookEntries LettrageModel[]
     */
    openLetteredBookEntriesDialog(letteredBookEntries: LettrageModel[]): Observable<string> {
        const pluriel = letteredBookEntries.length > 1;
        const config = new KalDialogConfig<LetteredBookEntriesDialogData>({
            title: 'écritures lettrées',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                letteredBookEntries,
                message: `${pluriel ? 'Les écritures sont lettrées' : 'L\'écriture est lettrée'}. En supprimant l'échéancier, le lettrage ${pluriel ? 'des écritures' : 'de l\'écriture'} ci-dessous sera supprimé.`
            } as LetteredBookEntriesDialogData
        });

        const dialogRef = this.dialogService.open(LetteredBookEntriesDialogComponent, config);

        return dialogRef.afterClosed.pipe(
            filter(e => !!e?.accepted),
            switchMap(() => {
                return this.graphqlService.mutateOne<'DelettrerEcritures', MutationDelettrerEcrituresArgs>({
                    mutation: gql`
                        mutation DelettrerEcritures($contexte: ContextInfosInput, $ListeIdEcritures: ArrayOfdecimalInput) {
                            DelettrerEcritures(
                                contexte: $contexte
                                ListeIdEcritures: $ListeIdEcritures
                            )
                        }
                    `,
                    variables: {
                        ListeIdEcritures: {
                            decimal: letteredBookEntries.map(l => +l.IdEcriture)
                        }
                    },
                });
            })
        );
    }

    // Suppression de l'échéancier
    deleteElement(scheduleId: number): Observable<string> {
        return this.graphqlService.mutateOne<'SupprimerEcheancier', MutationSupprimerEcheancierArgs>({
            mutation: gql`
                mutation DeleteSchedule($contexte: ContextInfosInput, $idEccheancier: Float) {
                    SupprimerEcheancier(
                        contexte: $contexte
                        idEccheancier: $idEccheancier
                    )
                }
            `,
            variables: {
                idEccheancier: +scheduleId
            }
        });
    }

    createRepaymentSchedule$(newSchedule: EcheancierModelDtoInput): Observable<number> {
        // on vérifie si il y a des écritures lettrées

        let action$: Observable<LettrageModel[]> = of([]);

        if (!!newSchedule.Id) {
            action$ = this.getLetteringEntries$(newSchedule.Id);
        }

        return action$.pipe(
            switchMap((letteredBookEntries) => {
                return arrayEmpty(letteredBookEntries)
                    ? of(null) // si pas d'écritures lettrées, on passe au prochain switchmap
                    : this.openLetteredBookEntriesDialog(letteredBookEntries);
            }),
            switchMap(() => this.saveSchedule$(newSchedule)),
        );
    }

    private saveSchedule$(schedulesList: EcheancierModelDtoInput): Observable<number> {
        return this.graphqlService.mutateOne<'UpdateEcheancier', MutationUpdateEcheancierArgs>({
            mutation: gql`
                mutation UpdateSchedule(
                    $contexte: ContextInfosInput,
                    $echeancier: EcheancierModelDTOInput,
                ) {
                    UpdateEcheancier(
                        contexte: $contexte
                        echeancier: $echeancier,
                    )
                }
            `,
            variables: {
                echeancier: schedulesList
            }
        });
    }
}
